import React, { useEffect, useMemo, useRef, useState } from "react";
import { useQuery } from "@apollo/client";

import TextEditor from "../../components/TextEditor";
import { installSavedFormulaRenderer } from "../../../utils/renderSavedFormulas";
import {
  hasMeaningfulRichHtml,
  normalizeRichHtmlForDisplay,
} from "../../../utils/richContent";
import { QUIZ_OPTIONS_QUERY } from "./tutorSubjectGraphql";
import {
  DEFAULT_ALTKLAUSUR_FORM5,
  DEFAULT_ALT_QUIZ_FORM5,
  DEFAULT_QUIZ_FORM5,
  DEFAULT_TASK_FORM5,
  Trash5,
  makeTempId5,
  normalizeDifficulty5,
  normalizeQuizType5,
  sortPos,
} from "./tutorSubjectHelpers";

// Tutor editor modals for lesson and Altklausur content.

export function DiscardChangesModal6({ open, onYes, onNo }) {
  if (!open) return null;

  return (
    <div className="lh6-modalOverlay" role="dialog" aria-modal="true">
      <div className="lh6-confirmCard">
        <div className="lh6-confirmText">
          Alle Änderungen, die du vorgenommen hast, gehen verloren. Bist du sicher?
        </div>

        <div className="lh6-confirmActions">
          <button type="button" className="lh6-btnNein" onClick={onNo}>
            Nein
          </button>
          <button type="button" className="lh6-btnJa" onClick={onYes}>
            Ja
          </button>
        </div>
      </div>
    </div>
  );
}

export function QuizOptionSummary5({ quizId }) {
  const { data } = useQuery(QUIZ_OPTIONS_QUERY, {
    variables: { quizId },
    skip: !quizId,
    fetchPolicy: "network-only",
  });

  const options = useMemo(
    () => (data?.quizOptionsByQuiz || []).slice().sort(sortPos),
    [data]
  );
  const correctCount = options.filter((opt) => opt.isCorrect).length;

  if (!quizId) return null;

  return (
    <div className="tutorR-optionSummary5">
      <span>{options.length || 0} Optionen</span>
      <span>{correctCount || 0} korrekt</span>
    </div>
  );
}


function RichQuestionField5({ value, onChange, editorTitle }) {
  const [editorOpen, setEditorOpen] = useState(false);
  const [editorDirty, setEditorDirty] = useState(false);
  const previewRootRef = useRef(null);

  useEffect(() => {
    if (!previewRootRef.current) return undefined;
    return installSavedFormulaRenderer(previewRootRef.current);
  }, [editorOpen, value]);

  const closeEditor = () => {
    if (editorDirty && !window.confirm("Änderungen an der Frage verwerfen?")) return;
    setEditorOpen(false);
    setEditorDirty(false);
  };

  const saveQuestion = async (html) => {
    onChange?.(html);
    setEditorOpen(false);
    setEditorDirty(false);
    return true;
  };

  return (
    <>
      <section className="tutorR-richPicker5 tutorR-richQuestion5">
        <div>
          <h3>Frage</h3>
          <div
            ref={previewRootRef}
            className="tutorR-richPreview5 lh-lessonContent"
            dangerouslySetInnerHTML={{
              __html: normalizeRichHtmlForDisplay(value, "Noch keine Frage."),
            }}
          />
        </div>
        <button
          type="button"
          className="tutorR-secondaryBtn5"
          onClick={() => {
            setEditorOpen(true);
            setEditorDirty(false);
          }}
        >
          Textbox öffnen
        </button>
      </section>

      <TextEditor
        open={editorOpen}
        initialHtml={value || ""}
        mode="edit"
        title={editorTitle || "Frage bearbeiten"}
        subtitle="Mit Bildern, KaTeX-Formeln und Textformatierung."
        onDirtyChange={(dirty) => setEditorDirty(!!dirty)}
        onSave={saveQuestion}
        onRequestClose={closeEditor}
      />
    </>
  );
}

export function QuizEditorModal5({ open, mode, quiz, onClose, onSave, saving }) {
  const { data: optionsData } = useQuery(QUIZ_OPTIONS_QUERY, {
    variables: { quizId: quiz?.id },
    skip: !open || mode !== "edit" || !quiz?.id,
    fetchPolicy: "network-only",
  });

  const [form, setForm] = useState(DEFAULT_QUIZ_FORM5);
  const [options, setOptions] = useState([]);
  const [initialOptions, setInitialOptions] = useState([]);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!open) return;

    setError("");
    setForm({
      ...DEFAULT_QUIZ_FORM5,
      ...(quiz || {}),
      questionType: normalizeQuizType5(quiz?.questionType),
      difficulty: normalizeDifficulty5(quiz?.difficulty),
      sourceType: quiz?.sourceType || "manual",
    });

    if (mode === "create") {
      const starter = ["", "", "", ""].map((optionText, index) => ({
        clientId: makeTempId5(),
        optionText,
        isCorrect: index === 0,
        position: index,
      }));
      setOptions(starter);
      setInitialOptions([]);
    } else {
      setOptions([]);
      setInitialOptions([]);
    }
  }, [mode, open, quiz]);

  useEffect(() => {
    if (!open || mode !== "edit") return;

    const next = (optionsData?.quizOptionsByQuiz || [])
      .slice()
      .sort(sortPos)
      .map((opt, index) => ({
        ...opt,
        clientId: opt.id || makeTempId5(),
        position: Number.isFinite(Number(opt.position)) ? Number(opt.position) : index,
      }));

    if (!optionsData) return;

    if (next.length > 0) {
      setOptions(next);
      setInitialOptions(next);
      return;
    }

    setOptions(
      ["", ""].map((optionText, index) => ({
        clientId: makeTempId5(),
        optionText,
        isCorrect: index === 0,
        position: index,
      }))
    );
    setInitialOptions([]);
  }, [mode, open, optionsData]);

  if (!open) return null;

  const updateForm = (patch) => setForm((prev) => ({ ...prev, ...patch }));

  const updateOption = (clientId, patch) => {
    setOptions((prev) =>
      prev.map((opt) => {
        if (opt.clientId !== clientId) {
          if (patch.isCorrect && form.questionType === "single_choice") {
            return { ...opt, isCorrect: false };
          }
          return opt;
        }
        return { ...opt, ...patch };
      })
    );
  };

  const addOption = () => {
    setOptions((prev) => [
      ...prev,
      {
        clientId: makeTempId5(),
        optionText: "",
        isCorrect: false,
        position: prev.length,
      },
    ]);
  };

  const removeOption = (clientId) => {
    setOptions((prev) => {
      const next = prev.filter((opt) => opt.clientId !== clientId);
      if (!next.some((opt) => opt.isCorrect) && next[0]) {
        next[0] = { ...next[0], isCorrect: true };
      }
      return next.map((opt, index) => ({ ...opt, position: index }));
    });
  };

  const submit = async () => {
    const cleanOptions = options
      .map((opt, index) => ({
        ...opt,
        optionText: (opt.optionText || "").trim(),
        position: index,
      }))
      .filter((opt) => opt.optionText);

    if (!hasMeaningfulRichHtml(form.question)) {
      setError("Bitte gib eine Frage ein.");
      return;
    }
    if (cleanOptions.length < 2) {
      setError("Bitte gib mindestens zwei Antwortoptionen ein.");
      return;
    }
    if (!cleanOptions.some((opt) => opt.isCorrect)) {
      setError("Bitte markiere mindestens eine richtige Antwort.");
      return;
    }

    setError("");
    await onSave?.({
      mode,
      quiz: {
        ...form,
        question: form.question || "",
        questionType: normalizeQuizType5(form.questionType),
        difficulty: normalizeDifficulty5(form.difficulty),
      },
      options: cleanOptions,
      initialOptions,
    });
  };

  return (
    <div className="tutorR-editorOverlay5" role="dialog" aria-modal="true">
      <div className="tutorR-quizModal5">
        <header className="tutorR-modalHead5">
          <div>
            <h2>{mode === "edit" ? "Quizfrage bearbeiten" : "Quizfrage erstellen"}</h2>
            <p>Frage, Antwortoptionen und Lernlogik festlegen.</p>
          </div>
          <button type="button" className="tutorR-iconBtn5" onClick={onClose} aria-label="Schließen">
            x
          </button>
        </header>

        <div className="tutorR-modalBody5">
          <RichQuestionField5
            value={form.question}
            onChange={(question) => updateForm({ question })}
            editorTitle="Quizfrage bearbeiten"
          />

          <div className="tutorR-modalGrid5">
            <label className="tutorR-field5">
              <span>Typ</span>
              <select
                value={form.questionType}
                onChange={(e) => {
                  const nextType = e.target.value;
                  updateForm({ questionType: nextType });
                  if (nextType === "single_choice") {
                    setOptions((prev) => {
                      const firstCorrect = Math.max(
                        prev.findIndex((opt) => opt.isCorrect),
                        0
                      );
                      return prev.map((opt, index) => ({
                        ...opt,
                        isCorrect: index === firstCorrect,
                      }));
                    });
                  }
                }}
              >
                <option value="single_choice">Single Choice</option>
                <option value="multiple_choice">Multiple Choice</option>
              </select>
            </label>

            <label className="tutorR-field5">
              <span>Schwierigkeit</span>
              <select
                value={form.difficulty}
                onChange={(e) => updateForm({ difficulty: e.target.value })}
              >
                <option value="einfach">Einfach</option>
                <option value="mittel">Mittel</option>
                <option value="schwer">Schwer</option>
              </select>
            </label>
          </div>

          <div className="tutorR-toggleRow5">
            <label>
              <input
                type="checkbox"
                checked={!!form.isKlausurRelevant}
                onChange={(e) => updateForm({ isKlausurRelevant: e.target.checked })}
              />
              <span>Klausurrelevant</span>
            </label>
            <label>
              <input
                type="checkbox"
                checked={form.requiredForCompletion !== false}
                onChange={(e) => updateForm({ requiredForCompletion: e.target.checked })}
              />
              <span>Pflicht für Abschluss</span>
            </label>
          </div>

          <section className="tutorR-optionsEditor5">
            <div className="tutorR-sectionTitleRow5">
              <h3>Antwortoptionen</h3>
              <span>Richtige Antwort markieren</span>
            </div>

            {options.map((opt, index) => (
              <div className="tutorR-optionRow5" key={opt.clientId || opt.id}>
                <span className="tutorR-optionIndex5">{index + 1}</span>
                <input
                  value={opt.optionText}
                  onChange={(e) => updateOption(opt.clientId, { optionText: e.target.value })}
                  placeholder="Antwortoption"
                />
                <input
                  type={form.questionType === "multiple_choice" ? "checkbox" : "radio"}
                  name="tutorR-correct-option"
                  checked={!!opt.isCorrect}
                  onChange={(e) => updateOption(opt.clientId, { isCorrect: e.target.checked })}
                  aria-label="Richtige Antwort"
                />
                <button
                  type="button"
                  className="tutorR-iconBtn5 danger5"
                  onClick={() => removeOption(opt.clientId)}
                  disabled={options.length <= 2}
                  aria-label="Antwort löschen"
                >
                  <Trash5 className="tutorR-menuSvg5" />
                </button>
              </div>
            ))}

            <button type="button" className="tutorR-addDashed5" onClick={addOption}>
              + Weitere Option
            </button>
          </section>

          <label className="tutorR-field5 full5">
            <span>Hinweis</span>
            <input
              value={form.hint || ""}
              onChange={(e) => updateForm({ hint: e.target.value })}
              placeholder="Optionaler Tipp für Studierende"
            />
          </label>

          <label className="tutorR-field5 full5">
            <span>Erklärung</span>
            <textarea
              value={form.explanation || ""}
              onChange={(e) => updateForm({ explanation: e.target.value })}
              placeholder="Optional: Warum ist die Antwort richtig?"
              rows={3}
            />
          </label>

          {error ? <div className="tutorR-formError5">{error}</div> : null}
        </div>

        <footer className="tutorR-modalFoot5">
          <button type="button" className="tutorR-secondaryBtn5" onClick={onClose}>
            Abbrechen
          </button>
          <button type="button" className="tutorR-primaryBtn5" onClick={submit} disabled={saving}>
            {saving ? "Speichern..." : "Speichern"}
          </button>
        </footer>
      </div>
    </div>
  );
}

export function TaskEditorModal5({
  open,
  mode,
  task,
  onClose,
  onSave,
  saving,
  contextLabel = "diese Lektion",
}) {
  const [form, setForm] = useState(DEFAULT_TASK_FORM5);
  const [richTarget, setRichTarget] = useState(null);
  const [richDirty, setRichDirty] = useState(false);
  const [error, setError] = useState("");
  const previewRootRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    setError("");
    setRichTarget(null);
    setRichDirty(false);
    setForm({
      ...DEFAULT_TASK_FORM5,
      ...(task || {}),
      title: task?.title || "",
      taskText: task?.taskText || "",
      hintText: task?.hintText || "",
      answerText: task?.answerText || "",
      difficulty: normalizeDifficulty5(task?.difficulty),
      estimatedMinutes: Number(task?.estimatedMinutes || 15),
      isPublished: task?.isPublished !== false,
    });
  }, [mode, open, task]);

  useEffect(() => {
    if (!open) return;

    return installSavedFormulaRenderer(previewRootRef.current);
  }, [form.answerText, form.taskText, open]);

  if (!open) return null;

  const updateForm = (patch) => setForm((prev) => ({ ...prev, ...patch }));

  const openRichEditor = (target) => {
    setRichTarget(target);
    setRichDirty(false);
  };

  const closeRichEditor = () => {
    if (richDirty && !window.confirm("Änderungen am Text verwerfen?")) return;
    setRichTarget(null);
    setRichDirty(false);
  };

  const saveRichText = async (html) => {
    if (!richTarget) return false;
    updateForm({ [richTarget]: html });
    setRichTarget(null);
    setRichDirty(false);
    return true;
  };

  const submit = async () => {
    if (!form.title.trim()) {
      setError("Bitte gib einen Titel für die Aufgabe ein.");
      return;
    }
    if (!String(form.taskText || "").trim()) {
      setError("Bitte gib einen Aufgabentext ein.");
      return;
    }

    setError("");
    await onSave?.({
      mode,
      task: {
        ...form,
        title: form.title.trim(),
        difficulty: normalizeDifficulty5(form.difficulty),
        estimatedMinutes: Math.max(0, Number(form.estimatedMinutes || 0)),
      },
    });
  };

  const richTitle =
    richTarget === "answerText" ? "Musterlösung bearbeiten" : "Aufgabentext bearbeiten";

  return (
    <div className="tutorR-editorOverlay5" role="dialog" aria-modal="true">
      <div className="tutorR-taskModal5">
        <header className="tutorR-modalHead5">
          <div>
            <h2>{mode === "edit" ? "Aufgabe bearbeiten" : "Aufgabe erstellen"}</h2>
            <p>Titel, Aufgabentext und Musterlösung für {contextLabel}.</p>
          </div>
          <button type="button" className="tutorR-iconBtn5" onClick={onClose} aria-label="Schließen">
            x
          </button>
        </header>

        <div ref={previewRootRef} className="tutorR-modalBody5">
          <div className="tutorR-modalGrid5 wideLeft5">
            <label className="tutorR-field5">
              <span>Titel der Aufgabe</span>
              <input
                value={form.title}
                onChange={(e) => updateForm({ title: e.target.value })}
                placeholder="z.B. Lineare Funktionen"
              />
            </label>

            <label className="tutorR-publishedSwitch5">
              <input
                type="checkbox"
                checked={!!form.isPublished}
                onChange={(e) => updateForm({ isPublished: e.target.checked })}
              />
              <span>Veröffentlicht</span>
            </label>
          </div>

          <section className="tutorR-richPicker5">
            <div>
              <h3>Aufgabe</h3>
              <div
                className="tutorR-richPreview5 lh-lessonContent lh-lessonContent-normalized"
                dangerouslySetInnerHTML={{ __html: form.taskText || "<p>Noch kein Aufgabentext.</p>" }}
              />
            </div>
            <button type="button" className="tutorR-secondaryBtn5" onClick={() => openRichEditor("taskText")}>
              Textbox öffnen
            </button>
          </section>

          <label className="tutorR-field5 full5">
            <span>Hinweis</span>
            <input
              value={form.hintText || ""}
              onChange={(e) => updateForm({ hintText: e.target.value })}
              placeholder="Optionaler Tipp"
            />
          </label>

          <section className="tutorR-richPicker5">
            <div>
              <h3>Musterlösung</h3>
              <div
                className="tutorR-richPreview5 lh-lessonContent lh-lessonContent-normalized"
                dangerouslySetInnerHTML={{ __html: form.answerText || "<p>Noch keine Musterlösung.</p>" }}
              />
            </div>
            <button type="button" className="tutorR-secondaryBtn5" onClick={() => openRichEditor("answerText")}>
              Textbox öffnen
            </button>
          </section>

          <div className="tutorR-modalGrid5">
            <label className="tutorR-field5">
              <span>Schwierigkeit</span>
              <select
                value={form.difficulty}
                onChange={(e) => updateForm({ difficulty: e.target.value })}
              >
                <option value="einfach">Einfach</option>
                <option value="mittel">Mittel</option>
                <option value="schwer">Schwer</option>
              </select>
            </label>

            <label className="tutorR-field5">
              <span>Geschätzte Zeit (Min)</span>
              <input
                type="number"
                min="0"
                value={form.estimatedMinutes}
                onChange={(e) => updateForm({ estimatedMinutes: e.target.value })}
              />
            </label>
          </div>

          {error ? <div className="tutorR-formError5">{error}</div> : null}
        </div>

        <footer className="tutorR-modalFoot5">
          <button type="button" className="tutorR-secondaryBtn5" onClick={onClose}>
            Abbrechen
          </button>
          <button type="button" className="tutorR-primaryBtn5" onClick={submit} disabled={saving}>
            {saving ? "Speichern..." : "Speichern"}
          </button>
        </footer>

        <TextEditor
          open={!!richTarget}
          initialHtml={richTarget ? form[richTarget] || "" : ""}
          mode="edit"
          title={richTitle}
          subtitle="Mit HTML-Ansicht, Formeln, Tabellen und freien Bildern."
          onDirtyChange={(d) => setRichDirty(!!d)}
          onSave={saveRichText}
          onRequestClose={closeRichEditor}
        />
      </div>
    </div>
  );
}

export function AltklausurMetaModal5({ open, mode, item, onClose, onSave, saving }) {
  const [form, setForm] = useState(DEFAULT_ALTKLAUSUR_FORM5);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!open) return;
    setError("");

    setForm({
      ...DEFAULT_ALTKLAUSUR_FORM5,
      ...(item || {}),
      title: item?.title || "",
      description: item?.description || "",
      isPublished: !!item?.isPublished,
    });
  }, [item, open]);

  if (!open) return null;

  const updateForm = (patch) => setForm((prev) => ({ ...prev, ...patch }));

  const submit = async () => {
    if (!form.title.trim()) {
      setError("Bitte gib einen Titel ein.");
      return;
    }

    setError("");
    await onSave?.({
      kind: "exam",
      mode,
      item: {
        ...form,
        title: form.title.trim(),
        description: form.description || "",
      },
    });
  };

  return (
    <div className="tutorR-editorOverlay5" role="dialog" aria-modal="true">
      <div className="tutorR-altMetaModal5">
        <header className="tutorR-modalHead5">
          <div>
            <h2>
              {mode === "edit" ? "Altklausur bearbeiten" : "Altklausur erstellen"}
            </h2>
            <p>Altklausur und Veröffentlichung festlegen.</p>
          </div>
          <button type="button" className="tutorR-iconBtn5" onClick={onClose} aria-label="Schließen">
            x
          </button>
        </header>

        <div className="tutorR-modalBody5">
          <label className="tutorR-field5 full5">
            <span>Titel</span>
            <input
              value={form.title}
              onChange={(e) => updateForm({ title: e.target.value })}
              placeholder="z.B. Physik I Klausur 2024"
            />
          </label>

          <label className="tutorR-field5 full5">
            <span>Beschreibung</span>
            <textarea
              value={form.description}
              onChange={(e) => updateForm({ description: e.target.value })}
              rows={4}
              placeholder="Kurze Orientierung für Studierende."
            />
          </label>

          <div className="tutorR-toggleRow5">
            <label>
              <input
                type="checkbox"
                checked={!!form.isPublished}
                onChange={(e) => updateForm({ isPublished: e.target.checked })}
              />
              <span>Veröffentlicht</span>
            </label>
          </div>

          {error ? <div className="tutorR-formError5">{error}</div> : null}
        </div>

        <footer className="tutorR-modalFoot5">
          <button type="button" className="tutorR-secondaryBtn5" onClick={onClose}>
            Abbrechen
          </button>
          <button type="button" className="tutorR-primaryBtn5" onClick={submit} disabled={saving}>
            {saving ? "Speichern..." : "Speichern"}
          </button>
        </footer>
      </div>
    </div>
  );
}

export function AltklausurQuizEditorModal5({
  open,
  mode,
  question,
  onClose,
  onSave,
  saving,
  titleOverride,
  subtitleOverride,
}) {
  const [form, setForm] = useState(DEFAULT_ALT_QUIZ_FORM5);
  const [options, setOptions] = useState([]);
  const [initialOptions, setInitialOptions] = useState([]);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!open) return;

    setError("");
    setForm({
      ...DEFAULT_ALT_QUIZ_FORM5,
      ...(question || {}),
      questionText: question?.questionText || "",
      questionType: normalizeQuizType5(question?.questionType),
      points: Math.max(1, Number(question?.points || 1)),
      sourceType: question?.sourceType || "tutor",
      hint: question?.hint || "",
      explanation: question?.explanation || "",
    });

    if (mode === "create") {
      const starter = ["", "", "", ""].map((optionText, index) => ({
        clientId: makeTempId5(),
        optionText,
        isCorrect: index === 0,
        position: index,
      }));
      setOptions(starter);
      setInitialOptions([]);
      return;
    }

    const next = (question?.options || [])
      .slice()
      .sort(sortPos)
      .map((opt, index) => ({
        ...opt,
        clientId: opt.id || makeTempId5(),
        position: Number.isFinite(Number(opt.position)) ? Number(opt.position) : index,
      }));

    if (next.length > 0) {
      setOptions(next);
      setInitialOptions(next);
      return;
    }

    const fallback = ["", ""].map((optionText, index) => ({
      clientId: makeTempId5(),
      optionText,
      isCorrect: index === 0,
      position: index,
    }));
    setOptions(fallback);
    setInitialOptions([]);
  }, [mode, open, question]);

  if (!open) return null;

  const updateForm = (patch) => setForm((prev) => ({ ...prev, ...patch }));

  const updateOption = (clientId, patch) => {
    setOptions((prev) =>
      prev.map((opt) => {
        if (opt.clientId !== clientId) {
          if (patch.isCorrect && form.questionType === "single_choice") {
            return { ...opt, isCorrect: false };
          }
          return opt;
        }
        return { ...opt, ...patch };
      })
    );
  };

  const addOption = () => {
    setOptions((prev) => [
      ...prev,
      {
        clientId: makeTempId5(),
        optionText: "",
        isCorrect: false,
        position: prev.length,
      },
    ]);
  };

  const removeOption = (clientId) => {
    setOptions((prev) => {
      const next = prev.filter((opt) => opt.clientId !== clientId);
      if (!next.some((opt) => opt.isCorrect) && next[0]) {
        next[0] = { ...next[0], isCorrect: true };
      }
      return next.map((opt, index) => ({ ...opt, position: index }));
    });
  };

  const submit = async () => {
    const cleanOptions = options
      .map((opt, index) => ({
        ...opt,
        optionText: (opt.optionText || "").trim(),
        position: index,
      }))
      .filter((opt) => opt.optionText);

    if (!hasMeaningfulRichHtml(form.questionText)) {
      setError("Bitte gib eine Frage ein.");
      return;
    }
    if (cleanOptions.length < 2) {
      setError("Bitte gib mindestens zwei Antwortoptionen ein.");
      return;
    }
    if (!cleanOptions.some((opt) => opt.isCorrect)) {
      setError("Bitte markiere mindestens eine richtige Antwort.");
      return;
    }

    setError("");
    await onSave?.({
      mode,
      question: {
        ...form,
        questionText: form.questionText || "",
        questionType: normalizeQuizType5(form.questionType),
        points: Math.max(1, Number(form.points || 1)),
      },
      options: cleanOptions,
      initialOptions,
    });
  };

  return (
    <div className="tutorR-editorOverlay5" role="dialog" aria-modal="true">
      <div className="tutorR-quizModal5">
        <header className="tutorR-modalHead5">
          <div>
            <h2>
              {titleOverride ||
                (mode === "edit" ? "Altklausur-Quiz bearbeiten" : "Altklausur-Quiz erstellen")}
            </h2>
            <p>{subtitleOverride || "Prüfungsfrage, Punkte und Antwortoptionen festlegen."}</p>
          </div>
          <button type="button" className="tutorR-iconBtn5" onClick={onClose} aria-label="Schließen">
            x
          </button>
        </header>

        <div className="tutorR-modalBody5">
          <RichQuestionField5
            value={form.questionText}
            onChange={(questionText) => updateForm({ questionText })}
            editorTitle="Altklausur-Quizfrage bearbeiten"
          />

          <div className="tutorR-modalGrid5">
            <label className="tutorR-field5">
              <span>Typ</span>
              <select
                value={form.questionType}
                onChange={(e) => {
                  const nextType = e.target.value;
                  updateForm({ questionType: nextType });
                  if (nextType === "single_choice") {
                    setOptions((prev) => {
                      const firstCorrect = Math.max(prev.findIndex((opt) => opt.isCorrect), 0);
                      return prev.map((opt, index) => ({
                        ...opt,
                        isCorrect: index === firstCorrect,
                      }));
                    });
                  }
                }}
              >
                <option value="single_choice">Single Choice</option>
                <option value="multiple_choice">Multiple Choice</option>
              </select>
            </label>

            <label className="tutorR-field5">
              <span>Punkte</span>
              <input
                type="number"
                min="1"
                value={form.points}
                onChange={(e) => updateForm({ points: e.target.value })}
              />
            </label>
          </div>

          <section className="tutorR-optionsEditor5">
            <div className="tutorR-sectionTitleRow5">
              <h3>Antwortoptionen</h3>
              <span>Richtige Antwort markieren</span>
            </div>

            {options.map((opt, index) => (
              <div className="tutorR-optionRow5" key={opt.clientId || opt.id}>
                <span className="tutorR-optionIndex5">{index + 1}</span>
                <input
                  value={opt.optionText}
                  onChange={(e) => updateOption(opt.clientId, { optionText: e.target.value })}
                  placeholder="Antwortoption"
                />
                <input
                  type={form.questionType === "multiple_choice" ? "checkbox" : "radio"}
                  name="tutorR-alt-correct-option"
                  checked={!!opt.isCorrect}
                  onChange={(e) => updateOption(opt.clientId, { isCorrect: e.target.checked })}
                  aria-label="Richtige Antwort"
                />
                <button
                  type="button"
                  className="tutorR-iconBtn5 danger5"
                  onClick={() => removeOption(opt.clientId)}
                  disabled={options.length <= 2}
                  aria-label="Antwort löschen"
                >
                  <Trash5 className="tutorR-menuSvg5" />
                </button>
              </div>
            ))}

            <button type="button" className="tutorR-addDashed5" onClick={addOption}>
              + Weitere Option
            </button>
          </section>

          <label className="tutorR-field5 full5">
            <span>Hinweis</span>
            <input
              value={form.hint || ""}
              onChange={(e) => updateForm({ hint: e.target.value })}
              placeholder="Optionaler Tipp für Studierende"
            />
          </label>

          <label className="tutorR-field5 full5">
            <span>Erklärung</span>
            <textarea
              value={form.explanation || ""}
              onChange={(e) => updateForm({ explanation: e.target.value })}
              placeholder="Optional: Warum ist die Antwort richtig?"
              rows={3}
            />
          </label>

          {error ? <div className="tutorR-formError5">{error}</div> : null}
        </div>

        <footer className="tutorR-modalFoot5">
          <button type="button" className="tutorR-secondaryBtn5" onClick={onClose}>
            Abbrechen
          </button>
          <button type="button" className="tutorR-primaryBtn5" onClick={submit} disabled={saving}>
            {saving ? "Speichern..." : "Speichern"}
          </button>
        </footer>
      </div>
    </div>
  );
}
